/**
 * LoadGenerationMix — the grid mix that served this load, as a stacked fuel bar
 * + legend. THE lens-driven view: the same load renders
 *   • Consequential → the marginal fossil that ramped (COL/NG/OIL only), and
 *   • Attributional → the full average generation mix that served it
 *     (incl. nuclear / hydro / solar / wind).
 * The two are visibly different by construction of the model, so switching the
 * dashboard lens changes this chart's shape and legend — with a one-line
 * explainer of the active basis.
 */

import { fuelSlices, fmtMWh } from "../fuel";

export interface LoadGenerationMixProps {
  /** grid_supply(.attributional).meta.per_fuel_mwh for the active lens. */
  perFuel?: Record<string, number>;
  /** grid_supply(.attributional).meta.grid_import_mwh. */
  importMwh?: number;
  lens: "consequential" | "attributional";
  analysisYear?: number;
  /** consequential marginal method label, when known (pinn / statistical). */
  marginalMethod?: string;
}

function methodLabel(m?: string): string {
  if (m === "pinn") return "marginal · PINN";
  if (m === "statistical") return "marginal · statistical";
  return "marginal";
}

export function LoadGenerationMix({
  perFuel,
  importMwh,
  lens,
  analysisYear,
  marginalMethod,
}: LoadGenerationMixProps) {
  const slices = fuelSlices(perFuel);
  const attr = lens === "attributional";

  return (
    <div className="card" style={{ margin: 0 }}>
      <div className="ch">
        <h2>Grid mix that served this load</h2>
        <span className="mut">
          {attr ? "average mix" : methodLabel(marginalMethod)}
          {analysisYear ? ` · ${analysisYear} grid` : ""}
        </span>
      </div>
      <div className="cb">
        <div className="lmix-basis">
          {attr ? (
            <>
              <b>Average-grid footprint</b> — the full average generation mix
              that served the load (a Scope-2 style view). Includes clean fuels
              (nuclear, hydro, wind, solar) alongside fossil.
            </>
          ) : (
            <>
              <b>Grid consequence (marginal)</b> — the specific units the grid
              ramped to serve this load, i.e. what changes if the load leaves.
              Marginal responders are fossil, so clean fuels don't appear here.
            </>
          )}
        </div>

        {importMwh != null && importMwh > 0 && (
          <div className="lmix-import">
            Drew <b>{fmtMWh(importMwh)}</b> from the grid
          </div>
        )}

        {slices.length > 0 ? (
          <>
            <div className="fuelbar">
              {slices.map((s) => (
                <span
                  key={s.label}
                  style={{ width: `${s.pct}%`, background: s.color }}
                  title={`${s.label} — ${s.pct.toFixed(0)}% · ${fmtMWh(s.mwh)}`}
                />
              ))}
            </div>
            <div className="lmix-rows">
              {slices.map((s) => (
                <div className="lmix-row" key={s.label}>
                  <span className="nm">
                    <i style={{ background: s.color }} />
                    {s.label}
                    {!attr && s.fossil && (
                      <span className="tagf">fossil</span>
                    )}
                  </span>
                  <span className="pct">{s.pct.toFixed(0)}%</span>
                  <span className="mwh">{fmtMWh(s.mwh)}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="empty">
            No fuel attribution available for this grid / period under the{" "}
            {attr ? "average-mix" : "marginal"} basis.
          </div>
        )}
      </div>
    </div>
  );
}
